import {handleAddToCart} from '@/cart/actions';
import ButtonLoadingIndicator from '@/components/ButtonLoadingIndicator';
import LoadingIndicator from '@/components/LoadingIndicator';
import Modal from '@/components/Modal';
import DeliverySelector from '@/components/buy-stack/DeliverySelector';
import SteppedButtonGroup from '@/components/buy-stack/SteppedButtonGroup';
import VariantSelector from '@/components/buy-stack/VariantSelector';
import ComponentToggleSignal from '@/components/component-toggle-signal';
import ProgressBar from '@/experiments/product-select-v3/ProgressBar';
import UpsellDialog from '@/experiments/upsell-dialog/UpsellDialog';
import {getFeatureFlag} from '@/feature-flags/feature-flags-store';
import {useHeight} from '@/hooks/use-height';
import {useNavigate} from '@/hooks/use-navigate';
import backArrowIcon from '@/icons/back-arrow-icon.svg';
import {getLocalizedStrings} from '@/localization/localization-store';
import {getProductSelection} from '@/product/product-selection';
import {hasPackOption} from '@/product/product-selection/pack-size';
import {SteppedProductSelectorSettings} from '@/settings/types';
import {colorStyleMap} from '@/utilities/color-style-map';
import {logError} from '@/utilities/log';
import {effect} from '@preact/signals';
import classNames from 'classnames';
import {useMemo, useRef, useState} from 'preact/hooks';
import AnimateHeight from 'react-animate-height';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import {endDrawer} from '../drawers';

export default SteppedProductSelector;

/**
 * Renders a stepped product selector component.
 * This is the primary interactive component in the rebranded buy-stack.
 * It allows customers to select product variants and selling plans in a step-by-step manner.
 *
 * @param settings - The settings for the stepped product selector.
 * @returns The rendered component.
 */
function SteppedProductSelector() {
  const isPostAtcUpsell = getFeatureFlag('post-atc') === 'true';

  const strings = getLocalizedStrings();

  const {product, selectedVariant, selectedPlan} = getProductSelection();

  const optionsFiltered =
    product?.options.filter((option) => option.values.length > 1) ?? [];
  const optionsCount = optionsFiltered.length;
  const totalSteps =
    product && hasPackOption(product) ? optionsCount : optionsCount + 1;
  const [currentStep, setCurrentStep] = useState(1);
  const option = optionsFiltered[currentStep - 1];
  const optionName = option?.name ?? '';
  const color = selectedVariant?.accentColor ?? '';
  const colorStyle = colorStyleMap.get(color);

  // Override button label for the first time offer
  // Otherwise, use stepped button labels
  const labelFirst = strings?.buyStackCtaLabelFirst ?? 'NEXT';
  const labelSecond = strings?.buyStackCtaLabelSecond ?? 'NEXT STEP';
  const steppedButtonLabel =
    currentStep === totalSteps ? labelSecond : labelFirst;
  const buttonLabel = steppedButtonLabel;

  const isDeliveryStep = currentStep === totalSteps;
  const variantStepRef = useRef<HTMLDivElement | null>(null);
  const deliveryStepRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const height = useHeight(containerRef);
  const upsellModalController = useMemo(() => new ComponentToggleSignal(), []);
  const upsellDialogSettings = product?.postAtcUpsellDialog;
  const navigate = useNavigate();

  // After the initial open, listen for the modal close
  // and navigate to cart
  if (upsellModalController.isOpen && isPostAtcUpsell) {
    effect(() => {
      if (!upsellModalController.isOpen) {
        navigate('/cart');
      }
    });
  }

  const handleClickNext = () => {
    const isLastStep = currentStep === totalSteps;
    if (isLastStep) {
      handleAddToCart(selectedVariant, selectedPlan).catch(logError);
    }

    // Open the upsell dialog on the last step if configured
    // If the upsell dialog is not configured, open the end drawer
    if (isPostAtcUpsell && isLastStep) {
      if (upsellDialogSettings?.product) {
        upsellModalController.open();
      } else {
        endDrawer.open();
      }
    }

    const nextStep = Math.min(currentStep + 1, totalSteps);
    setCurrentStep(nextStep);
  };

  const handleClickBack = () => {
    const nextStep = Math.max(currentStep - 1, 1);
    setCurrentStep(nextStep);
  };

  const isSoldOut = currentStep === totalSteps && !selectedVariant?.available;
  const soldOutLabel = strings?.buyStackSoldOut ?? 'Sold Out';

  return (
    product && (
      <>
        <div className="grid grid-cols-1 gap-y-1.5 text-center">
          <div className="justify-self-center">
            <ProgressBar
              currentStep={currentStep}
              stepsCount={
                // display an extra 'step' for checkout
                totalSteps + 1
              }
            />
          </div>
          <h6 className="col-span-full text-center text-sm font-semibold uppercase">
            {isDeliveryStep
              ? (strings.buyStackDeliverySelectorTitle ??
                'Choose Your Delivery')
              : optionName}
            :
            {isSoldOut && (
              <span
                className={classNames(
                  'ml-1 rounded-full bg-error-red px-2 py-0.5 align-middle text-xs text-crisp-white',
                  {
                    'opacity-0': !selectedVariant?.available === false,
                  }
                )}
              >
                {' '}
                {soldOutLabel}
              </span>
            )}
          </h6>
          <AnimateHeight height={height} duration={150} easing={'ease-in-out'}>
            <div ref={containerRef}>
              <TransitionGroup className="relative">
                {isDeliveryStep ? (
                  <CSSTransition
                    key="delivery"
                    timeout={500}
                    ref={deliveryStepRef}
                    classNames="fade"
                  >
                    <div className="pt-2.5">
                      <DeliverySelector formatMoneyWithDecimals={false} />
                    </div>
                  </CSSTransition>
                ) : (
                  option && (
                    <CSSTransition
                      key="variant"
                      timeout={500}
                      ref={variantStepRef}
                      classNames="fade"
                    >
                      <div className="pt-2.5">
                        <VariantSelector foldOptions option={option} />
                      </div>
                    </CSSTransition>
                  )
                )}
              </TransitionGroup>
            </div>
          </AnimateHeight>
          <div className="mt-2.5 justify-self-stretch">
            <ButtonLoadingIndicator
              button={
                <SteppedButtonGroup
                  label={isSoldOut ? soldOutLabel.toUpperCase() : buttonLabel}
                  onNextStep={handleClickNext}
                  onPreviousStep={handleClickBack}
                  previousIcon={backArrowIcon}
                  nextIcon={null}
                  disabled={
                    currentStep === totalSteps && !selectedVariant?.available
                  }
                  previousClassName="btn btn-square btn-neutral h-full w-16 border-none bg-bright-mist-gray"
                  nextClassName={classNames(
                    colorStyle?.button ?? 'btn-lime',
                    'btn btn-square w-full'
                  )}
                  showPreviousButton={currentStep > 1}
                />
              }
              fullWidth
              loader={<LoadingIndicator color={color} />}
            />
          </div>
        </div>
        {isPostAtcUpsell && upsellDialogSettings?.product && (
          <Modal controller={upsellModalController}>
            <UpsellDialog {...upsellDialogSettings} />
          </Modal>
        )}
      </>
    )
  );
}

export type SteppedProductSelectorProps = {
  settings: SteppedProductSelectorSettings;
};
